/** API calls for the derived secondary-connectivity detail panels (Phase 3 UX).
 *  Backend router: src/topology_platform/api/v1/secondary.py.
 *
 *  All GETs degrade to available:false (never 4xx) when no published run covers
 *  the entity, so callers can render a "not computed" state without try/catch. */

import { del, get, post } from './client.ts'
import type {
  SecondaryTracePath,
  TransformerSecondaryDetail,
} from '../types/secondary.ts'
import type {
  ComponentAcceptRequest,
  ComponentAcceptResponse,
  OverrideCreate,
  OverrideResponse,
  ReviewQueueResponse,
  WhyFlagged,
} from '../types/secondaryReview.ts'

function buildQS(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

/** The secondary leg of a meter's upstream trace (transformer -> secondary).
 *  Returns available:false when the meter's feeder has no published run. */
export function getMeterSecondaryTrace(
  meterId: number,
): Promise<SecondaryTracePath> {
  return get<SecondaryTracePath>(`/secondary/meters/${meterId}/secondary-trace`)
}

/** A transformer's connected secondary segments + meters. Override-aware:
 *  components re-rooted to this transformer appear; ones overridden away drop. */
export function getTransformerSecondary(
  transformerId: number,
): Promise<TransformerSecondaryDetail> {
  return get<TransformerSecondaryDetail>(
    `/secondary/transformers/${transformerId}/secondary`,
  )
}

// =============================================================================
// Review queue + manual overrides (Stage 4 write path)
// =============================================================================

/** The small ambiguous bucket (tier=review / unrooted / disagreement) to act on.
 *  Returns an empty page (total 0) until a published run covers a feeder. */
export function getReviewQueue(params?: {
  feeder_id?: number
  /** Narrow to one review reason SERVER-side (backend Task 10.1). Narrows
   *  `total` too, so a filtered page can quote an honest "N of M". */
  why_flagged?: WhyFlagged
  skip?: number
  limit?: number
}): Promise<ReviewQueueResponse> {
  return get<ReviewQueueResponse>(`/secondary/review-queue${buildQS(params ?? {})}`)
}

/** Record a manual connectivity decision (accept/reassign from the review UI).
 *  Always overrides at the component level (source_type='component'); the server
 *  stamps `created_by`. Live immediately via the read-time overlay. */
export function createOverride(body: OverrideCreate): Promise<OverrideResponse> {
  return post<OverrideResponse>('/secondary/overrides', body)
}

/** List manual connectivity overrides (newest first). */
export function listOverrides(params?: {
  active_only?: boolean
  skip?: number
  limit?: number
}): Promise<OverrideResponse[]> {
  return get<OverrideResponse[]>(`/secondary/overrides${buildQS(params ?? {})}`)
}

/** Deactivate (soft-delete) an override — removes its overlay effect at once. */
export function deactivateOverride(
  overrideId: number,
): Promise<OverrideResponse> {
  return del<OverrideResponse>(`/secondary/overrides/${overrideId}`)
}

/** Accept a component's root AND re-parent its meters in one transaction
 *  (Phase 5.1). 409 = stale page or a meter changed underneath; the detail
 *  names the meters. */
export function acceptComponent(body: ComponentAcceptRequest): Promise<ComponentAcceptResponse> {
  return post<ComponentAcceptResponse>('/secondary/components/accept', body)
}
